'use client';

import { Image, Typography } from '@/components/atoms';
import { PlayAllButton, List } from '@/components/molecules';
import { ContentTypeVariations } from '@/components/organisms';
import { Chapter } from '@/lib/contexts/media-controller/util';
import { useMediaController } from '@/lib/hooks/media-controller/useMediaController';
import { usePlayAll } from '@/lib/hooks/PlayAll/usePlayAll';
import { cn } from '@/lib/utils/tailwind';

import type { AccessContentPayload } from '@/lib/utils/access';
import type { ReactNode } from 'react';

export type PodcastDetail = AccessContentPayload & {
  id: number;
  title: string;
  image?: string;
  description: string;
  dateTime: string;
};

const Item = ({
  children,
  className,
  prefix,
  handleClickItem,
}: {
  children: ReactNode;
  className?: string;
  prefix: ReactNode;
  handleClickItem: (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLLIElement>
      | React.KeyboardEvent<HTMLLIElement>,
  ) => void | Promise<void>;
}) => {
  return (
    <List.Item
      className={cn(
        'border-0 border-b rounded-none',
        'flex w-full justify-between items-start px-(--track-list-vspacing-padding-lg) py-(--track-list-hspacing-padding-lg) border-b border-b-(--track-list-border-color-default) bg-(--track-list-bg-color-default) hover:bg-(--track-list-bg-color-hover) hover:shadow-md transition',
        className,
      )}
      prefix={prefix as string}
      onClick={handleClickItem}
    >
      {children}
    </List.Item>
  );
};

const ItemPrefix = ({
  imageUrl,
  title,
  rawData,
  startIndex,
  mediaType,
  chapterId,
}: {
  imageUrl?: string;
  title: string;
  rawData: {
    id: string;
    free_access?: boolean;
    subscription_tier?: string;
    allowed_roles?: string[];
    chapters: Chapter[];
  };
  startIndex: number;
  mediaType: ContentTypeVariations;
  chapterId: number;
}) => {
  const { trackIndex, currentCompilationType } = useMediaController();
  const isCurrentTrack =
    trackIndex === startIndex && mediaType === currentCompilationType;
  return (
    <List.ItemPrefix as={'span'} className={cn('relative w-16 h-16')}>
      <div className="group relative w-16 h-16">
        <Image
          src={imageUrl as string}
          alt={title}
          fill
          className="object-cover w-16 h-16"
        />
        <div
          className={cn(
            'absolute inset-0 transition-opacity duration-300 flex items-center justify-center',
            isCurrentTrack
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
          )}
        >
          <PlayAllButton
            mediaType={mediaType}
            rawData={rawData}
            startIndex={startIndex}
            trackId={chapterId}
          />
        </div>
      </div>
    </List.ItemPrefix>
  );
};

const Content = ({
  dateTime,
  title,
  description,
}: {
  dateTime: string;
  title: string;
  description: string;
}) => {
  return (
    <div className="flex flex-col gap-1 items-start self-stretch flex-1 min-w-0">
      <Typography.Text variant="body" size="sm" weight="regular">
        {dateTime}
      </Typography.Text>
      <Typography.Text
        variant="body"
        size="md"
        weight="semibold"
        className="line-clamp-3 overflow-hidden"
      >
        {title}
      </Typography.Text>
      <Typography.Text variant="paragraph" size="sm" weight="regular">
        {description}
      </Typography.Text>
    </div>
  );
};

export const PodcastTrackList = ({
  mediaType,
  tracks,
  rawData,
}: {
  mediaType: ContentTypeVariations;
  tracks: PodcastDetail[];
  rawData: {
    id: string;
    free_access?: boolean;
    subscription_tier?: 'basic' | 'premium' | 'vip';
    allowed_roles?: string[];
    chapters: Chapter[];
  };
}) => {
  const { handleClick } = usePlayAll({
    mediaType,
    rawData,
  });
  return (
    <List.Root
      type="custom"
      className={cn('flex flex-col items-center space-y-0')}
    >
      {tracks?.map((track, index) => {
        return (
          <Item
            className={cn('group')}
            key={index}
            prefix={
              <ItemPrefix
                imageUrl={track.image}
                title={track.title}
                mediaType={mediaType}
                rawData={rawData}
                startIndex={index}
                chapterId={track.id}
              />
            }
            handleClickItem={(
              e:
                | React.MouseEvent<HTMLButtonElement>
                | React.MouseEvent<HTMLLIElement>
                | React.KeyboardEvent<HTMLLIElement>,
            ) =>
              handleClick(
                e as unknown as React.MouseEvent<HTMLButtonElement>,
                index,
                track.id,
              )
            }
          >
            <Content
              title={track.title}
              dateTime={track.dateTime}
              description={track.description}
            />
          </Item>
        );
      })}
    </List.Root>
  );
};
